import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { firstValueFrom } from 'rxjs';

import { PageHeader } from '@core/components/page-header/page-header';
import { ServiceResponseV1 } from '@core/models/apis';
import {
  ProducerAdminListQuery,
  ProducerStatus,
  ProducerV1
} from '@core/models/producer-admin';
import { ProducersService } from '@core/services/producers';

const DEFAULT_PAGE_SIZE = 25;

interface StatusOption {
  label: string;
  value: ProducerStatus;
}

function isProducerStatus(value: string): value is ProducerStatus {
  return (Object.values(ProducerStatus) as string[]).includes(value);
}

@Component({
  selector: 'app-producers-list',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    TableModule,
    ButtonModule,
    IconField,
    InputIcon,
    InputTextModule,
    SelectModule,
    TagModule,
    MessageModule,
    SkeletonModule,
    PageHeader
  ],
  templateUrl: './producers-list.html',
  styleUrl: './producers-list.scss'
})
export class ProducersList {
  private readonly producersAPIs = inject(ProducersService);
  private readonly router = inject(Router);

  readonly status = input<string | undefined>(undefined);

  readonly page = signal<ServiceResponseV1<ProducerV1[]> | null>(null);
  readonly loading = signal<boolean>(false);
  readonly error = signal<boolean>(false);
  readonly searchTerm = signal<string>('');
  readonly selectedStatus = signal<ProducerStatus | null>(null);
  readonly sortField = signal<string>('createdOn');
  readonly sortOrder = signal<number>(-1);
  readonly pageNum = signal<number>(1);
  readonly pageSize = signal<number>(DEFAULT_PAGE_SIZE);

  readonly producers = this.producersAPIs.producers;
  readonly totalRecords = computed<number>(() => this.page()?.paging?.totalSize ?? 0);

  readonly statusOptions: StatusOption[] = [
    { label: 'Lead', value: ProducerStatus.Lead },
    { label: 'Pending', value: ProducerStatus.Pending },
    { label: 'Ready', value: ProducerStatus.Ready },
    { label: 'Active', value: ProducerStatus.Active }
  ];

  constructor() {
    effect(() => {
      const fromUrl = this.status();
      if (fromUrl && isProducerStatus(fromUrl)) {
        untracked(() => this.selectedStatus.set(fromUrl));
      }
    });

    effect(() => {
      const query: ProducerAdminListQuery = {
        pageNum: this.pageNum(),
        pageSize: this.pageSize(),
        search: this.searchTerm() || undefined,
        sort: `${this.sortField()}:${this.sortOrder() === 1 ? 'asc' : 'desc'}`,
        status: this.selectedStatus() ?? undefined
      };
      untracked(() => void this.loadProducers(query));
    });
  }

  hasActiveFilters(): boolean {
    return !!this.searchTerm().trim() || this.selectedStatus() !== null;
  }

  clearFilters(): void {
    this.searchTerm.set('');
    this.selectedStatus.set(null);
    this.pageNum.set(1);
  }

  applyFilters(): void {
    this.pageNum.set(1);
  }

  statusSeverity(producer: ProducerV1): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (producer.status) {
      case ProducerStatus.Active:
        return 'success';
      case ProducerStatus.Ready:
        return 'info';
      case ProducerStatus.Pending:
        return 'warn';
      case ProducerStatus.Lead:
        return 'secondary';
      case ProducerStatus.Suspended:
        return 'danger';
      case ProducerStatus.Deactivated:
        return 'contrast';
      default:
        return 'secondary';
    }
  }

  openDetail(producer: ProducerV1): void {
    this.router.navigate(['/producers', producer.id]);
  }

  onSort(event: { field: string; order: number }): void {
    this.sortField.set(event.field);
    this.sortOrder.set(event.order);
  }

  onPage(event: { first: number; rows: number }): void {
    this.pageNum.set(Math.floor(event.first / event.rows) + 1);
    this.pageSize.set(event.rows);
  }

  navigateTo(paths: string[]): void {
    this.router.navigate(paths);
  }

  private async loadProducers(query: ProducerAdminListQuery): Promise<void> {
    this.loading.set(true);
    this.error.set(false);

    try {
      const response = await firstValueFrom(this.producersAPIs.getProducersList(query));
      this.page.set(response);
    } catch {
      this.error.set(true);
    } finally {
      this.loading.set(false);
    }
  }
}
